/**
 * Real-time State Atoms
 *
 * Jotai atoms for managing real-time data streams:
 * - Position updates
 * - WebSocket connection state
 * - Real-time subscriptions
 * - Stream processing
 */

import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';
import { RealtimeSubscription } from '@/lib/placeholder-types';
import type { DomainEvent, RealtimePosition } from '@/lib/domain-events';

// WebSocket connection state
export const webSocketConnectionAtom = atom<{
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  factoryId: string | null;
  error: string | null;
  reconnectAttempts: number;
  lastConnected: Date | null;
}>({
  status: 'disconnected',
  factoryId: null,
  error: null,
  reconnectAttempts: 0,
  lastConnected: null,
});

// Active subscriptions
export const activeSubscriptionsAtom = atom<RealtimeSubscription[]>([]);

// Real-time position data (by asset)
export const realtimePositionsAtom = atomWithReset<Map<string, RealtimePosition[]>>(new Map());

// Latest position for each asset
export const latestPositionsAtom = atom(get => {
  const positions = get(realtimePositionsAtom);
  const latest = new Map<string, RealtimePosition>();

  positions.forEach((assetPositions, assetId) => {
    if (assetPositions.length > 0) {
      // Positions are stored in descending order (newest first)
      latest.set(assetId, assetPositions[0]);
    }
  });

  return latest;
});

// Staleness thresholds
export const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 min → idle/stale
export const OFFLINE_THRESHOLD_MS = 60 * 60 * 1000; // 60 min → offline/not reporting

/**
 * Detect idle positions (assets that haven't reported in 5–60 min).
 * Used for visual indicators and alerts.
 */
export const stalePositionsAtom = atom(get => {
  const latest = get(latestPositionsAtom);
  const now = Date.now();
  const stale = new Set<string>();

  latest.forEach((pos, assetId) => {
    if (now - pos.timestamp > IDLE_THRESHOLD_MS) {
      stale.add(assetId);
    }
  });

  return stale;
});

/**
 * Detect offline positions (assets that haven't reported in > 60 min).
 */
export const offlinePositionsAtom = atom(get => {
  const latest = get(latestPositionsAtom);
  const now = Date.now();
  const offline = new Set<string>();

  latest.forEach((pos, assetId) => {
    if (now - pos.timestamp > OFFLINE_THRESHOLD_MS) {
      offline.add(assetId);
    }
  });

  return offline;
});

// Whether usePositionBuffer is mounted (Tracking page). When true, WebSocketProvider
// skips feeding raw positions to the atom so they don't conflict with the
// coordinate-transformed positions from the buffer.
export const positionBufferActiveAtom = atom(false);

// Position update statistics
export const positionStatsAtom = atom<{
  totalUpdates: number;
  updatesPerMinute: number;
  lastUpdateTime: Date | null;
  activeAssets: number;
}>({
  totalUpdates: 0,
  updatesPerMinute: 0,
  lastUpdateTime: null,
  activeAssets: 0,
});

// Asset IDs the Tracking page knows about (null = no guard). While set, position
// writes keyed by anything else are dropped. Raw hardware tag IDs reach the store
// through several paths (WebSocketProvider's untranslated feed while the buffer
// is off, buffer pass-through before the tag map loads) and a one-shot cleanup
// races them — leftovers doubled the asset count until refresh (DEV2-1519).
export const knownAssetIdsAtom = atom<Set<string> | null>(null);

function keepKnown(
  knownAssetIds: Set<string> | null,
  positions: RealtimePosition[]
): RealtimePosition[] {
  return knownAssetIds ? positions.filter(p => knownAssetIds.has(p.assetId)) : positions;
}

// Real-time event stream
export const eventStreamAtom = atomWithReset<DomainEvent[]>([]);

// Add position update
export const addPositionUpdateAtom = atom(null, (get, set, position: RealtimePosition): void => {
  if (keepKnown(get(knownAssetIdsAtom), [position]).length === 0) {
    return;
  }

  const currentPositions = get(realtimePositionsAtom);
  const assetPositions = currentPositions.get(position.assetId) || [];

  // Add new position at the beginning (newest first)
  const updatedPositions = [position, ...assetPositions.slice(0, 99)]; // Keep last 100 positions

  // Update positions map
  const newPositionsMap = new Map(currentPositions);
  newPositionsMap.set(position.assetId, updatedPositions);
  set(realtimePositionsAtom, newPositionsMap);

  // Update statistics — use Date.now() (time of receipt), not position.timestamp,
  // so the connection health badge reflects when we last heard from the edge device.
  const currentStats = get(positionStatsAtom);
  set(positionStatsAtom, {
    ...currentStats,
    totalUpdates: currentStats.totalUpdates + 1,
    lastUpdateTime: new Date(),
    activeAssets: newPositionsMap.size,
  });
});

// Batch position updates
export const addPositionBatchAtom = atom(null, (get, set, batch: RealtimePosition[]): void => {
  const positions = keepKnown(get(knownAssetIdsAtom), batch);
  const currentPositions = get(realtimePositionsAtom);
  const newPositionsMap = new Map(currentPositions);

  // Group positions by asset
  const positionsByAsset = new Map<string, RealtimePosition[]>();
  positions.forEach(position => {
    if (!positionsByAsset.has(position.assetId)) {
      positionsByAsset.set(position.assetId, []);
    }
    const assetPositions = positionsByAsset.get(position.assetId);
    if (assetPositions) {
      assetPositions.push(position);
    }
  });

  // Update each asset's position history
  positionsByAsset.forEach((newPositions, assetId) => {
    const existingPositions = newPositionsMap.get(assetId) || [];
    // Sort by timestamp descending and merge with existing
    const sortedNewPositions = newPositions.sort((a, b) => b.timestamp - a.timestamp);
    const mergedPositions = [...sortedNewPositions, ...existingPositions].slice(0, 100);
    newPositionsMap.set(assetId, mergedPositions);
  });

  set(realtimePositionsAtom, newPositionsMap);

  // Update statistics — use Date.now() (time of receipt), not position timestamps,
  // so the connection health badge reflects when we last heard from the edge device.
  const currentStats = get(positionStatsAtom);
  set(positionStatsAtom, {
    ...currentStats,
    totalUpdates: currentStats.totalUpdates + positions.length,
    lastUpdateTime: new Date(),
    activeAssets: newPositionsMap.size,
  });
});

// Get positions for specific asset
export const getAssetPositionsAtom = atom(
  null,
  (get, _set, assetId: string): RealtimePosition[] => {
    const positions = get(realtimePositionsAtom);
    return positions.get(assetId) || [];
  }
);

// Get latest position for asset
export const getAssetLatestPositionAtom = atom(
  null,
  (get, _set, assetId: string): RealtimePosition | null => {
    const latestPositions = get(latestPositionsAtom);
    return latestPositions.get(assetId) || null;
  }
);

// Connection management
export const setConnectionStatusAtom = atom(
  null,
  (
    get,
    set,
    status: 'disconnected' | 'connecting' | 'connected' | 'error',
    error?: string
  ): void => {
    const current = get(webSocketConnectionAtom);
    set(webSocketConnectionAtom, {
      ...current,
      status,
      error: error || null,
      lastConnected: status === 'connected' ? new Date() : current.lastConnected,
    });
  }
);

// Set connected factory
export const setConnectedFactoryAtom = atom(null, (get, set, factoryId: string): void => {
  const current = get(webSocketConnectionAtom);
  set(webSocketConnectionAtom, {
    ...current,
    factoryId,
    reconnectAttempts: 0,
  });
});

// Increment reconnection attempts
export const incrementReconnectAttemptsAtom = atom(null, (get, set): void => {
  const current = get(webSocketConnectionAtom);
  set(webSocketConnectionAtom, {
    ...current,
    reconnectAttempts: current.reconnectAttempts + 1,
  });
});

// Reset reconnection attempts
export const resetReconnectAttemptsAtom = atom(null, (get, set): void => {
  const current = get(webSocketConnectionAtom);
  set(webSocketConnectionAtom, {
    ...current,
    reconnectAttempts: 0,
  });
});

// Subscription management
export const addSubscriptionAtom = atom(
  null,
  (get, set, subscription: RealtimeSubscription): void => {
    const current = get(activeSubscriptionsAtom);
    const exists = current.find(s => s.id === subscription.id);

    if (!exists) {
      set(activeSubscriptionsAtom, [...current, subscription]);
    }
  }
);

export const removeSubscriptionAtom = atom(null, (get, set, subscriptionId: string): void => {
  const current = get(activeSubscriptionsAtom);
  set(
    activeSubscriptionsAtom,
    current.filter(s => s.id !== subscriptionId)
  );
});

export const clearSubscriptionsAtom = atom(null, (_get, set): void => {
  set(activeSubscriptionsAtom, []);
});

// Event stream management
export const addEventToStreamAtom = atom(null, (get, set, event: DomainEvent): void => {
  const current = get(eventStreamAtom);
  // Keep last 1000 events
  const updated = [event, ...current.slice(0, 999)];
  set(eventStreamAtom, updated);
});

export const clearEventStreamAtom = atom(null, (_get, set): void => {
  set(eventStreamAtom, []);
});

// Get recent events by type
export const getRecentEventsByTypeAtom = atom(
  null,
  (get, _set, eventType: string, limit: number = 10): DomainEvent[] => {
    const events = get(eventStreamAtom);
    return events.filter(event => event.type === eventType).slice(0, limit);
  }
);

// Connection health indicators
export const connectionHealthAtom = atom(get => {
  const connection = get(webSocketConnectionAtom);
  const stats = get(positionStatsAtom);
  const subscriptions = get(activeSubscriptionsAtom);

  const isHealthy =
    connection.status === 'connected' &&
    connection.reconnectAttempts < 3 &&
    subscriptions.length > 0;

  const lastUpdateAge = stats.lastUpdateTime ? Date.now() - stats.lastUpdateTime.getTime() : null;

  return {
    status: connection.status,
    isHealthy,
    reconnectAttempts: connection.reconnectAttempts,
    lastUpdateAge,
    activeSubscriptions: subscriptions.length,
    activeAssets: stats.activeAssets,
    updatesPerMinute: stats.updatesPerMinute,
  };
});

// Stream quality metrics
export const streamQualityAtom = atom(get => {
  const stats = get(positionStatsAtom);
  const connection = get(webSocketConnectionAtom);

  // Calculate updates per minute (simple approximation)
  const now = Date.now();
  const lastUpdate = stats.lastUpdateTime?.getTime();
  const timeSinceLastUpdate = lastUpdate ? now - lastUpdate : 0;

  let quality: 'excellent' | 'good' | 'poor' | 'disconnected';
  if (connection.status !== 'connected') {
    quality = 'disconnected';
  } else if (timeSinceLastUpdate < 30000) {
    // < 30 seconds
    quality = 'excellent';
  } else if (timeSinceLastUpdate < 120000) {
    // < 2 minutes
    quality = 'good';
  } else {
    quality = 'poor';
  }

  return {
    quality,
    timeSinceLastUpdate,
    totalUpdates: stats.totalUpdates,
    activeAssets: stats.activeAssets,
  };
});

// Asset tracking status with active/idle/offline counts
export const assetTrackingStatusAtom = atom(get => {
  const latest = get(latestPositionsAtom);
  const stale = get(stalePositionsAtom);
  const offline = get(offlinePositionsAtom);

  const idleCount = stale.size - offline.size;

  return {
    total: latest.size,
    active: latest.size - stale.size,
    idle: idleCount,
    offline: offline.size,
    stale: stale.size,
    idleThresholdMinutes: IDLE_THRESHOLD_MS / 60000,
    offlineThresholdMinutes: OFFLINE_THRESHOLD_MS / 60000,
  };
});

// Set (or clear with null) the known asset IDs and purge any other entries already stored
export const setKnownAssetIdsAtom = atom(
  null,
  (get, set, knownAssetIds: Set<string> | null): void => {
    set(knownAssetIdsAtom, knownAssetIds);
    if (!knownAssetIds) {
      return;
    }

    const currentPositions = get(realtimePositionsAtom);
    const filtered = new Map<string, RealtimePosition[]>();
    currentPositions.forEach((positions, assetId) => {
      if (knownAssetIds.has(assetId)) {
        filtered.set(assetId, positions);
      }
    });
    set(realtimePositionsAtom, filtered);
    const currentStats = get(positionStatsAtom);
    set(positionStatsAtom, { ...currentStats, activeAssets: filtered.size });
  }
);

// Clear positions only (calibration change — dots re-appear at corrected positions)
export const clearPositionsAtom = atom(null, (_get, set): void => {
  set(realtimePositionsAtom, new Map());
  set(positionStatsAtom, {
    totalUpdates: 0,
    updatesPerMinute: 0,
    lastUpdateTime: null,
    activeAssets: 0,
  });
});

// Reset all real-time state (factory switch, logout)
export const resetRealtimeStateAtom = atom(null, (_get, set): void => {
  set(realtimePositionsAtom, new Map());
  set(eventStreamAtom, []);
  set(activeSubscriptionsAtom, []);
  set(positionStatsAtom, {
    totalUpdates: 0,
    updatesPerMinute: 0,
    lastUpdateTime: null,
    activeAssets: 0,
  });
  set(webSocketConnectionAtom, {
    status: 'disconnected',
    factoryId: null,
    error: null,
    reconnectAttempts: 0,
    lastConnected: null,
  });
});
